import React, { useCallback } from 'react';
import { Modal } from 'antd';
import styles from './useConfirmModal.module.css';
import classNames from 'classnames';
import capitalize from 'capitalize';
import useTranslation from '@hooks/useTranslation';
import { CloseCircleFilled, InfoCircleFilled, WarningFilled } from '@ant-design/icons';


type ConfirmModalTuple = [confirm: (config: {
  title: React.ReactNode;
  content: React.ReactNode;
  okText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  onOk: () => void;
  onCancel?: () => void;
}) => void, contextHolder: React.ReactNode];

/**
 * A hook to create a styled confirm modal
 * @returns [confirm, contextHolder]
 */
const useConfirmModal = (): ConfirmModalTuple => {
  const [modal, contextHolder] = Modal.useModal();
  const t = useTranslation('CommonWords');

  const confirm = useCallback((config: { 
    title: React.ReactNode;
    content: React.ReactNode; 
    okText?: string;
    cancelText?: string;
    type?: 'danger' | 'warning' | 'info';
    onOk: () => void;
    onCancel?: () => void;
  }) => {
    const { title, content, type='info', onOk, onCancel, okText, cancelText } = config;
    
    modal.confirm({
      className: classNames(styles.ConfirmModal, styles[capitalize(type) as keyof typeof styles]),
      title,
      icon: type === 'danger' ? <CloseCircleFilled /> : type === 'warning' ? <WarningFilled /> : <InfoCircleFilled />,
      okText: okText || t('Ok'),
      cancelText: cancelText || t('Cancel'),
      okButtonProps: {
        className: classNames(styles.ConfirmModalButton, {
          [styles.OkButton]: type === 'info',
          [styles.WarningButton]: type === 'warning',
          [styles.DangerButton]: type === 'danger',
        }),
      },
      cancelButtonProps: {
        className: classNames(styles.ConfirmModalButton, styles.CancelButton),  
      },
      content: content,
      onOk,
      onCancel: onCancel || (() => {}),
    });
  }, [modal]);

  return [confirm, contextHolder];
};

export default useConfirmModal;